import React, { useState, useMemo, useEffect, useRef } from "react";
import styled from "styled-components";
import { Slider, Typography, Box, CircularProgress } from "@mui/material";
import { breakpoint, theme } from "@utilities";
import { formatPrice, isDisplayablePrice } from "src/Utilities/priceHelper";

interface RewardSliderProps {
    earnedPoints: number;
    unUsedPoints: number;
    totalPoints?: number;
    pointsPerDollar: number;
    productValue: number;
    onChange?: (usedPoints: number) => void;
    finalPrice?: number;
    originalPrice?: number;
}

export const RewardSlider: React.FC<RewardSliderProps> = ({
    earnedPoints,
    unUsedPoints,
    totalPoints,
    pointsPerDollar,
    productValue,
    onChange,
    finalPrice,
    originalPrice,
}) => {
    const [usedPoints, setUsedPoints] = useState<number>(0);
    /**
     * The points breakdown starts collapsed. It is reference detail — the figures
     * that matter to the decision are the "You pay" line and the slider below it,
     * and four stat tiles above them push those off the first screen.
     */
    const [isBreakdownOpen, setIsBreakdownOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false); // State to track if amount is being edited
    const [editValue, setEditValue] = useState(""); // State to store the input value during editing

    // helper to round to 2 decimal places (numeric)
    const round2 = (n: number) => Math.round(n * 100) / 100;

    // Calculate the maximum points that can be used based on product value
    const maxPointsFromProductValue = Math.floor(productValue * pointsPerDollar);

    // Available points (raw) and then rounded to 2 decimals for UI and slider precision
    const availablePointsRaw = Math.min(
        Math.max(0, unUsedPoints + earnedPoints),
        maxPointsFromProductValue,
    );
    const availablePoints = round2(availablePointsRaw);

    // Open at the most the customer can redeem rather than at zero, then keep
    // usedPoints clamped if availablePoints later shrinks. availablePoints is 0
    // for the first render or two while the balance and order total load, so seed
    // on the first non-zero value rather than on mount.
    const hasSeededRef = useRef(false);
    useEffect(() => {
        if (!hasSeededRef.current && availablePoints > 0) {
            hasSeededRef.current = true;
            setUsedPoints(availablePoints);
            onChange?.(availablePoints);
            return;
        }
        if (usedPoints > availablePoints) {
            setUsedPoints(availablePoints);
            onChange?.(availablePoints);
        }
    }, [availablePoints]);

    // Format points: compact for big numbers, otherwise show up to 2 decimals
    const compactFmt = new Intl.NumberFormat("en", {
        notation: "compact",
        compactDisplay: "short",
        maximumFractionDigits: 3,
        minimumFractionDigits: 0,
    });

    // normal formatter for non-compact values (up to 2 decimals)
    const fixedFmt = new Intl.NumberFormat("en", {
        maximumFractionDigits: 2,
        minimumFractionDigits: 0,
        useGrouping: true,
    });

    const formatPoints = (pts: number) => {
        const abs = Math.abs(pts);
        // use compact for thousands and above
        if (abs >= 1000) {
            return compactFmt.format(pts).toLowerCase(); // "1.696K" -> "1.696k"
        }
        // otherwise show full grouped number with up to 2 decimals
        return fixedFmt.format(round2(pts));
    };

    // generate marks - only show initial (0) and final (max) points
    const marks = useMemo(() => {
        if (availablePoints === 0) return [];
        return [
            { value: 0, label: "0" },
            { value: availablePoints, label: formatPoints(availablePoints) },
        ];
    }, [availablePoints]);

    // step granularity: two decimal places
    const stepValue = 0.01;

    const handleSliderChange = (_: any, val: number | number[]) => {
        const pts = Array.isArray(val) ? val[0] : val;
        const rounded = round2(pts);
        setUsedPoints(rounded);
        onChange?.(rounded);
    };

    // Function to handle pencil icon click and enter edit mode
    const handleEditClick = () => {
        setEditValue((productValue - usedPoints / pointsPerDollar).toFixed(2)); // Set current amount as edit value
        setIsEditing(true); // Enable edit mode
    };

    // Function to handle edit submission and calculate new points
    const handleEditSubmit = () => {
        const newAmount = parseFloat(editValue); // Parse the entered amount
        if (!isNaN(newAmount) && newAmount >= 0 && newAmount <= productValue) {
            // Validate input
            const newPoints = (productValue - newAmount) * pointsPerDollar; // Calculate points from amount
            const clampedPoints = Math.min(Math.max(0, newPoints), availablePoints); // Clamp to valid range
            setUsedPoints(round2(clampedPoints)); // Update used points
            onChange?.(round2(clampedPoints)); // Notify parent component
        }
        setIsEditing(false); // Exit edit mode
    };

    // Function to cancel editing and reset state
    const handleEditCancel = () => {
        setIsEditing(false); // Exit edit mode
        setEditValue(""); // Clear edit value
    };
    /**
     * The caller may not pass prices at all, in which case this section is not
     * meant to render. When it does pass them, they can still be NaN while the
     * cart totals are being derived — and `!== undefined` waves NaN straight
     * through, which is how "$NaN" reached the You pay line.
     */
    const hasPriceProps = finalPrice !== undefined && originalPrice !== undefined;
    const arePricesReady = isDisplayablePrice(finalPrice) && isDisplayablePrice(originalPrice);

    return (
        <Container>
            <BreakdownToggle
                type="button"
                onClick={() => setIsBreakdownOpen((open) => !open)}
                aria-expanded={isBreakdownOpen}
            >
                <span>Your points</span>
                <BreakdownChevron $isOpen={isBreakdownOpen} aria-hidden="true">
                    &#9662;
                </BreakdownChevron>
            </BreakdownToggle>
            <Header hidden={!isBreakdownOpen}>
                {totalPoints !== undefined && (
                    <div className="stat">
                        <div className="label">Total</div>
                        <div className="value slider_total">{formatPoints(totalPoints)} pts</div>
                    </div>
                )}
                <div className="stat">
                    <div className="label">Available</div>
                    <div className="value slider_text1">
                        {formatPoints(Math.max(0, unUsedPoints))} pts
                    </div>
                </div>
                {totalPoints !== undefined && (
                    <div className="stat">
                        <div className="label">Pending</div>
                        <div className="value slider_pending">
                            {formatPoints(totalPoints - unUsedPoints)} pts
                        </div>
                    </div>
                )}
                <div className="stat">
                    <div className="label">This order</div>
                    <div className="value slider_text">
                        +{formatPoints(Math.max(0, earnedPoints))} pts
                    </div>
                </div>
            </Header>

            <SliderSection>
                {availablePoints === 0 ? (
                    <Box sx={{ py: 3, textAlign: "center" }}>
                        <Typography variant="body1" color="text.secondary">
                            No points are available
                        </Typography>
                    </Box>
                ) : (
                    <div>
                        <YouPayLabel>
                            <span>You pay</span>
                            <span>{formatPoints(usedPoints)} pts</span>
                            <span>+</span>
                            {isEditing ? ( // Conditional rendering: show input when editing
                                <EditInput
                                    type="number" // Number input type
                                    value={editValue} // Controlled input value
                                    onChange={(e) => setEditValue(e.target.value)} // Update edit value on change
                                    onBlur={handleEditSubmit} // Submit on blur
                                    onKeyDown={(e) => {
                                        // Handle keyboard events
                                        if (e.key === "Enter") handleEditSubmit(); // Submit on Enter
                                        if (e.key === "Escape") handleEditCancel(); // Cancel on Escape
                                    }}
                                    autoFocus // Auto focus when editing starts
                                />
                            ) : (
                                <YouPayAmount>
                                    {" "}
                                    {/* Display amount when not editing. Guarded
                                        like the totals below: productValue can be
                                        NaN before the cart resolves, and a zero
                                        pointsPerDollar makes the division
                                        infinite. */}
                                    {formatPrice(productValue - usedPoints / pointsPerDollar) ?? (
                                        <CircularProgress size={14} />
                                    )}
                                </YouPayAmount>
                            )}
                            {!isEditing && (
                                <img
                                    src="/images/Pencil.svg"
                                    alt="edit"
                                    width={14}
                                    height={14}
                                    onClick={handleEditClick} // Click handler to start editing
                                    style={{ cursor: "pointer" }} // Pointer cursor for better UX
                                />
                            )}
                        </YouPayLabel>
                        <Slider
                            value={usedPoints}
                            onChange={handleSliderChange}
                            min={0}
                            max={availablePoints}
                            step={stepValue}
                            marks={marks}
                            valueLabelDisplay="auto"
                            valueLabelFormat={(v) => `${formatPoints(v)} pts`}
                            sx={{
                                width: "100%",
                                height: 8,
                                "& .MuiSlider-root": {
                                    height: 8,
                                },
                                "& .MuiSlider-rail": {
                                    height: 10,
                                    borderRadius: 6,
                                    // A tint of the brand colour, so the unfilled part
                                    // of the bar stays in the same family as the track
                                    // in both flavors rather than a fixed pale shade
                                    // that only suits one of them.
                                    background: `${theme.colors.main}2E`,
                                    opacity: 1,
                                    "&::before, &::after": {
                                        content: '""',
                                        position: "absolute",
                                        width: 6,
                                        height: 6,
                                        backgroundColor: "white",
                                        borderRadius: "50%",
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        zIndex: 1,
                                    },
                                    "&::before": {
                                        left: 2,
                                    },
                                    "&::after": {
                                        right: 2,
                                    },
                                },
                                "& .MuiSlider-track": {
                                    height: 10,
                                    borderRadius: 6,
                                    // Two-tone once the customer spends past what this
                                    // booking earns: the brand colour covers the earned
                                    // points and the muted one the balance drawn down
                                    // behind them.
                                    background:
                                        usedPoints <= earnedPoints
                                            ? theme.colors.main
                                            : `linear-gradient(90deg, ${theme.colors.main} ${(earnedPoints / usedPoints) * 100}%, ${theme.colors.main_disable} ${(earnedPoints / usedPoints) * 100}%)`,
                                    border: "none",
                                    "&::before, &::after": {
                                        content: '""',
                                        position: "absolute",
                                        width: 6,
                                        height: 6,
                                        backgroundColor: "white",
                                        borderRadius: "50%",
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        zIndex: 1,
                                    },
                                    "&::before": {
                                        left: 2,
                                    },
                                    "&::after": {
                                        right: 2,
                                    },
                                },
                                "& .MuiSlider-thumb": {
                                    width: 20,
                                    height: 20,
                                    /* Matches the track it rides on. */
                                    backgroundColor: theme.colors.main,
                                },
                                "& .MuiSlider-mark": {
                                    display: "none",
                                },
                                "& .MuiSlider-markLabel": {
                                    fontSize: "12px",
                                    color: "#898d8f",
                                    position: "absolute",
                                    top: "calc(100% + 8px)",
                                    whiteSpace: "nowrap",
                                    fontFamily: "Poppins, sans-serif",
                                },
                                "& .MuiSlider-valueLabel": {
                                    fontSize: "12px",
                                    fontWeight: 500,
                                    top: -6,
                                    backgroundColor: "#6E7375",
                                    borderRadius: "4px",
                                    padding: "4px 8px",
                                },
                                "@media (pointer: coarse)": {
                                    padding: "0 !important",
                                },
                            }}
                        />
                    </div>
                )}
            </SliderSection>
            {hasPriceProps && (
                <>
                    {/* Paid and saved share one row: they are two halves of the
                        same sentence, and the saving reads as the reason for the
                        figure beside it rather than as a separate statistic. */}
                    <TotalsRow>
                        <PriceSection>
                            <div className="price-label">You pay</div>
                            <div className="price-display">
                                {arePricesReady ? (
                                    <>
                                        {usedPoints > 0 && (
                                            <span className="price-strikethrough">
                                                {formatPrice(originalPrice)}
                                            </span>
                                        )}
                                        <span className="price-value">
                                            {formatPrice(finalPrice)}
                                        </span>
                                    </>
                                ) : (
                                    <CircularProgress size={18} />
                                )}
                            </div>
                        </PriceSection>
                        <SavedGroup>
                            <span className="label">You saved</span>
                            {/* The gap between the two figures beside it, rather
                                than the points converted at the rate: those can
                                differ by rounding, and a saving that does not
                                match the prices next to it reads as an error. */}
                            <span className="value">
                                {arePricesReady ? (
                                    formatPrice(Math.max(0, originalPrice! - finalPrice!))
                                ) : (
                                    <CircularProgress size={14} />
                                )}
                            </span>
                        </SavedGroup>
                    </TotalsRow>
                </>
            )}
        </Container>
    );
};

/** styled-components */
const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    width: 100%;
`;

const BreakdownToggle = styled.button`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 8px 10px;
    border: 1px solid #e6e9eb;
    border-radius: 8px;
    background: #fbfdfe;
    font-family: "Poppins", sans-serif;
    font-size: 12px;
    font-weight: 600;
    color: ${theme.colors.gray_1};
    cursor: pointer;
`;

const BreakdownChevron = styled.span<{ $isOpen: boolean }>`
    display: inline-block;
    font-size: 12px;
    line-height: 1;
    transition: transform 150ms ease;
    transform: rotate(${(p) => (p.$isOpen ? "180deg" : "0deg")});
`;

const Header = styled.div`
    font-family: "Poppins", sans-serif;
    /* The hidden attribute collapses this when the toggle is closed; the grid
       rule below would otherwise out-specify the attribute's own display:none. */
    &[hidden] {
        display: none;
    }
    margin-top: 8px;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    align-items: center;
    border: 1px solid #e6e9eb;
    border-radius: 8px;
    text-align: center;
    overflow: hidden;
    background: #fbfdfe;

    & > .stat {
        flex: 1;
        padding: 10px 6px;
        display: flex;
        flex-direction: column;
        gap: 6px;
        justify-content: center;
        align-items: center;
        background: transparent;
        border-right: 1px solid #e6e9eb;

        &:last-child {
            border-right: none;
        }
    }

    & .label {
        font-size: 11px;
        font-weight: 400;
        line-height: 80%;
        color: #131214;
    }

    & .value {
        font-size: 15px;
        font-weight: 500;
        line-height: 120%;
    }

    /* Only "This order" keeps a colour — it is the figure this booking changes.
       Total, Available and Pending are reference numbers and read better black
       than as three unrelated hues. */
    & .slider_text {
        color: ${theme.colors.red};
    }
    & .slider_text1,
    & .slider_total,
    & .slider_pending {
        color: ${theme.colors.black};
    }
`;

const SliderSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    width: 100%;
    padding: 0 10px 0px 10px;
    ${breakpoint.breakOnlyMobile`
        padding: 0 14px 0px 14px;
    `}
`;

const YouPayLabel = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    font-family: "Poppins";
    font-size: 14px;
    line-height: 120%;
    color: #131214;
    margin-bottom: 8px;

    span {
        font-weight: 500;
    }

    img {
        cursor: pointer;
    }
`;

const YouPayAmount = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 6px 7px;
    font-weight: 700;
    font-size: 14px;
    line-height: 120%;
    color: ${theme.colors.black};
`;

const EditInput = styled.input`
    padding: 6px 7px;
    border: 1px solid #e6e9eb;
    border-radius: 4px;
    font-weight: 600;
    font-size: 14px;
    line-height: 120%;
    width: 80px;
    text-align: center;
    font-family: "Poppins";
    color: ${theme.colors.highlight};
    &:focus {
        outline: none;
        border-color: ${theme.colors.highlight};
    }
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }
    &[type="number"] {
        -moz-appearance: textfield;
    }
`;

const PriceSection = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    /* Keeps the label off its own figures now that this group sizes to content
       rather than spanning the row. */
    gap: 14px;
    padding: 0;
    width: 100%;

    .price-label {
        font-weight: 500;
        font-size: 18px;
        line-height: 120%;
        color: #131214;
    }

    .price-display {
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .price-strikethrough {
        position: relative;
        font-weight: 400;
        font-size: 16px;
        line-height: 120%;
        color: #898d8f;
        ${theme.colors.price
            ? `
            text-decoration: none;
            &::after {
                content: '';
                position: absolute;
                left: -2px;
                right: -2px;
                top: 0;
                bottom: 0;
                background: linear-gradient(
                    to top left,
                    transparent calc(50% - 1px),
                    ${theme.colors.price} calc(50% - 1px),
                    ${theme.colors.price} calc(50% + 1px),
                    transparent calc(50% + 1px)
                );
            }
        `
            : `text-decoration: line-through;`}
    }

    .price-value {
        font-weight: 600;
        font-size: 18px;
        line-height: 120%;
        color: ${theme.colors.price || theme.colors.highlight};
    }
`;

const TotalsRow = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 32px;
    width: 100%;
    /* Lifted clear of the slider's 0 / max labels directly above, which otherwise
       crowd the totals into looking like part of the scale. */
    margin-top: 22px;
    padding-top: 18px;
    border-top: 1px solid #e6e9eb;

    /* The You pay half sizes to its content here rather than filling the row, so
       the two groups sit at the ends instead of the pay label drifting inwards.
       Matched on the component rather than a position, so reordering the row does
       not silently detach it. */
    ${PriceSection} {
        width: auto;
        flex: 0 1 auto;
    }

    ${breakpoint.breakOnlyMobile`
        flex-wrap: wrap;
        gap: 12px;
    `}
`;

const SavedGroup = styled.div`
    display: flex;
    align-items: baseline;
    gap: 10px;
    font-family: "Poppins", sans-serif;
    white-space: nowrap;

    .label {
        font-size: 18px;
        font-weight: 500;
        line-height: 120%;
        color: #131214;
    }

    /* Deliberately the loudest thing on the row: the saving is the reason the
       points were spent, and at 14px it read as a footnote to the price. */
    .value {
        font-size: 22px;
        font-weight: 700;
        line-height: 120%;
        color: ${theme.colors.red};
    }
`;
